import { useState, useEffect, useCallback } from "react";
import { AlertTriangle, X, Loader2, RefreshCw, CheckCircle2 } from "lucide-react";
import { reenviarPendentes } from "../lib/database";
import { pendentes } from "../lib/filaGravacao";

const ROTULO = {
  clients: "clientes", partners: "parceiros", tasks: "tarefas",
  contracts: "contratos", satisfaction: "pesquisas de satisfação",
  management_reports: "relatórios", credenciamentos: "fichas de credenciamento",
  pesquisa_config: "configuração de pesquisa",
};

/**
 * O que não gravou, na cara de quem está usando.
 *
 * A gravação acontece em segundo plano: a tela mostra o resultado antes de o
 * banco confirmar. Quando falhava, o erro ia para o console e a pessoa só
 * descobria no F5 seguinte — a alteração tinha sumido, sem explicação.
 *
 * Aqui aparecem duas coisas diferentes, e a distinção importa:
 *   · fila — o dado está guardado no navegador e sobe sozinho na próxima
 *     abertura. Ninguém precisa refazer nada; o aviso é para saber.
 *   · campo sem coluna — bug de programação. A linha gravou sem aquele campo,
 *     e alguém do TI precisa criar a coluna. Some ao recarregar.
 *   · tabela inexistente — o módulo inteiro grava num lugar que nunca foi
 *     criado. Nada ali é salvo, e ninguém percebia: foi o que aconteceu com
 *     os relatórios de gestão. Aqui a pessoa descobre ANTES de digitar.
 */
export default function AvisoSyncFalhou() {
  const [fila, setFila] = useState(() => pendentes().length);
  const [campos, setCampos] = useState(null);
  const [semTabela, setSemTabela] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [enviouAgora, setEnviouAgora] = useState(0);
  const [fechado, setFechado] = useState(false);

  const tentar = useCallback(async () => {
    setEnviando(true);
    const r = await reenviarPendentes().catch(() => ({ enviados: 0, restantes: pendentes().length }));
    setEnviando(false);
    setFila(r.restantes);
    if (r.enviados) { setEnviouAgora(r.enviados); setTimeout(() => setEnviouAgora(0), 6000); }
  }, []);

  // Na abertura, sobe o que ficou para trás. É o momento em que a rede
  // costuma estar de volta — e é o que torna a fila útil de verdade.
  useEffect(() => { if (pendentes().length) tentar(); }, [tentar]);

  useEffect(() => {
    const mudou = (e) => { setFila(e.detail?.total ?? pendentes().length); setFechado(false); };
    const falhou = () => { setFila(pendentes().length); setFechado(false); };
    const campoRuim = (e) => { setCampos(e.detail); setFechado(false); };
    const tabelaRuim = (e) => { setSemTabela(e.detail); setFechado(false); };
    window.addEventListener("crm-fila-mudou", mudou);
    window.addEventListener("crm-sync-falhou", falhou);
    window.addEventListener("crm-campo-desconhecido", campoRuim);
    window.addEventListener("crm-tabela-inexistente", tabelaRuim);
    return () => {
      window.removeEventListener("crm-fila-mudou", mudou);
      window.removeEventListener("crm-sync-falhou", falhou);
      window.removeEventListener("crm-campo-desconhecido", campoRuim);
      window.removeEventListener("crm-tabela-inexistente", tabelaRuim);
    };
  }, []);

  if (fechado) return null;
  if (!fila && !campos && !semTabela && !enviouAgora) return null;

  const cor = fila || campos || semTabela ? { fundo: "#fef2f2", borda: "#fecaca", texto: "#7f1d1d", icone: "#dc2626" }
    : { fundo: "#f0fdf4", borda: "#bbf7d0", texto: "#166534", icone: "#059669" };

  return (
    <div
      role="alert"
      style={{
        position: "fixed", zIndex: 9999, left: 16, right: 16, bottom: 16,
        maxWidth: 560, margin: "0 auto", display: "flex", gap: 10,
        alignItems: "flex-start", padding: "12px 14px", borderRadius: 10,
        background: cor.fundo, border: `1px solid ${cor.borda}`,
        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
      }}
    >
      {fila || campos || semTabela
        ? <AlertTriangle size={17} color={cor.icone} style={{ flexShrink: 0, marginTop: 1 }} />
        : <CheckCircle2 size={17} color={cor.icone} style={{ flexShrink: 0, marginTop: 1 }} />}

      <div style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: cor.texto, lineHeight: 1.5 }}>
        {fila > 0 && (
          <>
            <strong>{fila === 1 ? "1 alteração ainda não subiu" : `${fila} alterações ainda não subiram`}</strong> para o
            servidor. Estão guardadas aqui no navegador e vão sozinhas na próxima abertura — não precisa refazer.
            <button
              onClick={tentar}
              disabled={enviando}
              style={{ display: "inline-flex", alignItems: "center", gap: 5, marginTop: 8, padding: "5px 11px", borderRadius: 7, border: "1px solid #fecaca", background: "#fff", color: "#b91c1c", fontSize: 11, fontWeight: 600, cursor: enviando ? "wait" : "pointer" }}
            >
              {enviando ? <><Loader2 size={12} className="spin" /> Enviando...</> : <><RefreshCw size={12} /> Tentar agora</>}
            </button>
          </>
        )}

        {!fila && enviouAgora > 0 && (
          <><strong>Pronto.</strong> {enviouAgora === 1 ? "1 alteração pendente subiu" : `${enviouAgora} alterações pendentes subiram`} para o servidor.</>
        )}

        {semTabela && (
          <div style={{ marginTop: fila ? 8 : 0 }}>
            <strong>Este módulo não está salvando</strong>: a tabela de {ROTULO[semTabela.tabela] || semTabela.tabela} não
            existe no banco. O que for preenchido aqui não será guardado. Mande esta linha para o TI.
          </div>
        )}

        {campos && (
          <div style={{ marginTop: fila ? 8 : 0 }}>
            <strong>Um campo não existe no banco</strong> ({ROTULO[campos.tabela] || campos.tabela}): {campos.campos?.join(", ")}.
            O resto foi salvo normalmente. Mande esta linha para o TI — é preciso criar a coluna.
          </div>
        )}
      </div>

      <button
        onClick={() => setFechado(true)}
        aria-label="Fechar aviso"
        style={{ background: "none", border: "none", cursor: "pointer", color: cor.icone, display: "flex", padding: 2 }}
      >
        <X size={15} />
      </button>
      <style>{`.spin { animation: spin 1s linear infinite } @keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  );
}
